import React, { useState } from "react";
import { motion } from "framer-motion";
import { Code2, LineChart, Filter, ArrowRight, Sparkles } from "lucide-react";
import CodingRequestModal from "@/components/landing/CodingRequestModal";

const OFFERINGS = [
  {
    id: "algo",
    icon: Code2,
    badge: "FOR CUSTOM ALGO STRATEGIES",
    title: "Algo Strategy Development",
    body:
      "Have a unique trading logic that generic parameters can't capture? Our engineers will code it for you — multi-leg, multi-condition, with your own SL/target rules and adjustments.",
    price: "₹14,999",
    priceNote: "Starting from",
    image:
      "https://images.pexels.com/photos/6770610/pexels-photo-6770610.jpeg?auto=compress&cs=tinysrgb&w=900",
    accent: "#084392",
    perks: [
      "Strategy translated to code",
      "Backtested on historical data",
      "Deployed on your Tradecure account or local PC or AWS",
      "1-on-1 walkthrough call",
    ],
  },
  {
    id: "indicator",
    icon: LineChart,
    badge: "FOR INDICATOR BUILDERS",
    title: "Custom Indicator Development",
    body:
      "Want a brand-new indicator, or a tweak to an existing one (RSI, MACD, SuperTrend) with your own rules and thresholds? We build it — clean, fast, and tradable.",
    price: "₹7,999",
    priceNote: "Starting from",
    image:
      "https://images.pexels.com/photos/7567228/pexels-photo-7567228.jpeg?auto=compress&cs=tinysrgb&w=900",
    accent: "#10b981",
    perks: [
      "Built-in or hybrid logic",
      "Custom alerts & signals",
      "Backtest-ready",
      "Source delivered to you",
    ],
  },
  {
    id: "screener",
    icon: Filter,
    badge: "FOR STOCK FILTERING",
    title: "Custom Screener Development",
    body:
      "Filter the universe by your own rules — fundamentals, technicals, options-OI, or a combination. We build the screener that surfaces exactly the stocks you want.",
    price: "₹7,999",
    priceNote: "Starting from",
    image:
      "https://images.pexels.com/photos/590011/pexels-photo-590011.jpeg?auto=compress&cs=tinysrgb&w=900",
    accent: "#f59e0b",
    perks: [
      "Any-condition filtering",
      "NSE / BSE universe support",
      "Daily / intraday refresh",
      "Exportable results",
    ],
  },
];

export default function CodingServices() {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState("");

  const launch = (service = "") => {
    setPreset(service);
    setOpen(true);
  };

  return (
    <section
      id="build-for-me"
      data-testid="coding-services-section"
      className="relative py-20 md:py-28 bg-white border-y border-[#e2e8f0] overflow-hidden"
    >
      <div className="absolute inset-0 grid-bg opacity-50 pointer-events-none" />
      <div className="absolute -top-32 -left-32 h-[420px] w-[420px] rounded-full bg-[#084392]/8 blur-3xl pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-5 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-[#e2e8f0] bg-white px-3 py-1.5 text-xs font-semibold text-[#084392] font-mono tracking-[0.2em]">
            <Sparkles size={12} /> BUILD-FOR-ME
          </div>
          <h2 className="font-heading mt-5 text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1]">
            Got a unique strategy?
            <br />
            <span className="text-[#084392]">We&rsquo;ll code it for you.</span>
          </h2>
          <p className="mt-5 text-lg text-[#475569]">
            Some ideas don&rsquo;t fit a template. If your edge needs custom
            logic, a bespoke indicator, or a screener built around your own
            filters — our engineers will build it on Tradecure, end-to-end.
          </p>
        </motion.div>

        <div className="mt-12 grid md:grid-cols-3 gap-5">
          {OFFERINGS.map((o, i) => {
            const Icon = o.icon;
            return (
              <motion.article
                key={o.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                data-testid={`coding-card-${o.id}`}
                className="group rounded-2xl border border-[#e2e8f0] bg-white overflow-hidden hover:border-[#084392]/40 hover:-translate-y-1 transition-all duration-300 flex flex-col"
              >
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={o.image}
                    alt={o.title}
                    className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/25 to-transparent" />
                  <div className="absolute top-4 left-4 right-4 flex items-start justify-between">
                    <span className="text-[10px] font-mono font-bold text-white tracking-[0.18em] bg-black/30 backdrop-blur-sm rounded px-2 py-1">
                      {o.badge}
                    </span>
                    <div
                      className="h-10 w-10 rounded-lg flex items-center justify-center backdrop-blur-sm"
                      style={{ background: `${o.accent}55`, color: "white" }}
                    >
                      <Icon size={18} />
                    </div>
                  </div>
                  <div className="absolute bottom-4 left-4 right-4">
                    <h3 className="font-heading text-2xl font-bold text-white tracking-tight">
                      {o.title}
                    </h3>
                  </div>
                </div>

                <div className="p-6 flex flex-col flex-1">
                  <p className="text-[#475569] leading-relaxed">{o.body}</p>

                  <ul className="mt-4 space-y-1.5 text-sm">
                    {o.perks.map((p, j) => (
                      <li key={j} className="flex items-start gap-2 text-[#334155]">
                        <span
                          className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0"
                          style={{ background: o.accent }}
                        />
                        {p}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-auto pt-5 flex items-end justify-between">
                    <div>
                      <div className="text-[10px] font-mono text-[#475569] tracking-[0.15em]">
                        {o.priceNote.toUpperCase()}
                      </div>
                      <div className="font-heading text-3xl font-black text-[#0f172a] tracking-tighter">
                        {o.price}
                      </div>
                    </div>
                    <button
                      onClick={() => launch(o.title)}
                      data-testid={`coding-cta-${o.id}`}
                      className="inline-flex items-center gap-1.5 h-11 px-4 rounded-md bg-[#084392] hover:bg-[#06326e] text-white text-sm font-semibold transition"
                    >
                      Request quote <ArrowRight size={14} />
                    </button>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </div>

        {/* Bottom CTA bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-10 rounded-2xl border border-[#084392]/20 bg-[#084392] text-white p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5"
        >
          <div>
            <h3 className="font-heading text-2xl md:text-3xl tracking-tight">
              Not sure which one you need?
            </h3>
            <p className="mt-2 text-white/80 max-w-xl">
              Tell us what you&rsquo;re trying to build. We&rsquo;ll respond
              within 24 business hours with a free scoping plan and a fixed
              quote.
            </p>
          </div>
          <button
            onClick={() => launch("")}
            data-testid="coding-cta-discuss"
            className="shrink-0 inline-flex items-center gap-2 rounded-md bg-white text-[#084392] font-bold h-12 px-6 hover:bg-[#f8fafc] transition"
          >
            Discuss my project <ArrowRight size={16} />
          </button>
        </motion.div>
      </div>

      <CodingRequestModal open={open} onClose={() => setOpen(false)} preset={preset} />
    </section>
  );
}
